import { create } from "zustand";
import { TransactionData } from "@/types/types";
import { getUserGymId } from "@/utils/gymAuth";
import {
  getPaymentSummary,
  getPaymentsWithSubscription,
  PaymentSummaryRow,
} from "@/services/edgeFunctionWrappers/paymentsManagementWrapper";

// États de couverture
export type CoverageState = "unpaid" | "partial" | "paid" | "overpaid" | "ignored";

export interface SubscriptionPaymentSummary {
  // Identité
  subscriptionId: string;
  userId: string;
  firstName: string | null;
  lastName: string | null;

  // Abonnement
  subscriptionType: string | null;
  startDate: string;        // yyyy-mm-dd
  endDate: string;          // yyyy-mm-dd
  subscriptionPrice: number;
  subscriptionStatus: string | null;
  paymentMethod: string | null;
  paymentFrequency: string | null;
  isPaused: boolean;
  isManuallyActivated: boolean;

  // === Métriques "COMPLETED only" (back-compat) ===
  totalPaid: number;
  paymentsCount: number;
  lastPaymentDate: string | null;       // ISO datetime
  coverageState: CoverageState;         // basé completed
  coveragePct: number;

  // === Nouvelles métriques PENDING-AWARE (completed + pending) ===
  pendingAmount: number;
  pendingCount: number;
  lastPendingDate: string | null;

  totalIncludingPending: number;
  paymentsCountIncludingPending: number;
  lastPaymentDateIncludingPending: string | null;
  coverageStateIncludingPending: CoverageState;
  coveragePctIncludingPending: number;
}

interface PaymentSummaryState {
  summaries: SubscriptionPaymentSummary[];
  isLoading: boolean;
  error: string | null;

  /**
   * Charge les summaries.
   * @param opts.effective - si true, les champs "de base" (totalPaid, paymentsCount, lastPaymentDate, coverageState, coveragePct)
   *                         seront remplis à partir des colonnes INCLUDING_PENDING. Par défaut false (COMPLETED only).
   * @param opts.onlyActive - filtre status=active côté vue. Par défaut true.
   */
  fetchSummaries: (opts?: { effective?: boolean; onlyActive?: boolean }) => Promise<void>;
  fetchPaymentsForSubscription: (subscriptionId: string) => Promise<TransactionData[]>;
}

export const usePaymentSummaryStore = create<PaymentSummaryState>((set) => ({
  summaries: [],
  isLoading: false,
  error: null,

  fetchPaymentsForSubscription: async (subscriptionId: string) => {
    const gymId = getUserGymId();
    const { data, error } = await getPaymentsWithSubscription(gymId, subscriptionId);
  
    if (error) {
      console.error("fetchPaymentsForSubscription error:", error);
      return [];
    }
  
    // map vers TransactionData
    const mapped: TransactionData[] = (data || []).map((row) => ({
      id: row.payment_id,
      client: `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim() || "—",
      amount: Number(row.payment_amount ?? 0),
      date: row.payment_date ? new Date(row.payment_date).toISOString().split("T")[0] : "",
      status: row.payment_status || "pending",
      type: row.payment_description || "Payment",
      paymentMethod: row.payment_method || "cash",
      description: row.payment_description || "Payment",
      // @ts-ignore
      subscriptionId: row.subscription_id || undefined,
      // @ts-ignore
      userId: row.user_id,
    }));
  
    return mapped;
  },

  fetchSummaries: async (opts) => {
    const effective = opts?.effective ?? false;   // false = completed-only (back-compat), true = completed+pending
    const onlyActive = opts?.onlyActive ?? false; // à appliquer côté edge function

    set({ isLoading: true, error: null });
    try {
      const gymId = getUserGymId();
      const { data, error } = await getPaymentSummary(gymId, onlyActive);
      
      if (error) throw error;

      const rows: PaymentSummaryRow[] = data || [];

      const mapped: SubscriptionPaymentSummary[] = rows.map((r) => {
        // Base : toutes les colonnes exposées par la vue
        const completed_totalPaid  = Number(r.total_paid ?? 0);
        const completed_count      = Number(r.payments_count ?? 0);
        const completed_last       = r.last_payment_date ?? null;
        const completed_state      = (r.coverage_state ?? "ignored") as CoverageState;
        const completed_pct        = Number(r.coverage_pct ?? 0);

        const pending_amount       = Number(r.pending_amount ?? 0);
        const pending_count        = Number(r.pending_count ?? 0);
        const pending_last         = r.last_pending_date ?? null;

        const any_total            = Number(r.total_including_pending ?? 0);
        const any_count            = Number(r.payments_count_including_pending ?? 0);
        const any_last             = r.last_payment_date_including_pending ?? null;
        const any_state            = (r.coverage_state_including_pending ?? "ignored") as CoverageState;
        const any_pct              = Number(r.coverage_pct_including_pending ?? 0);

        // Selon le mode choisi, on "remplit" les champs de base à partir des colonnes voulues
        const base_totalPaid       = effective ? any_total : completed_totalPaid;
        const base_count           = effective ? any_count  : completed_count;
        const base_last            = effective ? any_last   : completed_last;
        const base_state           = effective ? any_state  : completed_state;
        const base_pct             = effective ? any_pct    : completed_pct;

        return {
          subscriptionId: r.subscription_id,
          userId: r.user_id,
          firstName: r.first_name ?? null,
          lastName: r.last_name ?? null,
          subscriptionType: r.subscription_type ?? null,
          startDate: r.start_date,
          endDate: r.end_date,
          subscriptionPrice: Number(r.subscription_price ?? 0),
          subscriptionStatus: r.subscription_status ?? null,
          paymentMethod: r.payment_method ?? null,
          paymentFrequency: r.payment_frequency ?? null,
          isPaused: !!r.is_paused,
          isManuallyActivated: !!r.is_manually_activated,

          // Champs "base" (dépendent de effective)
          totalPaid: base_totalPaid,
          paymentsCount: base_count,
          lastPaymentDate: base_last,
          coverageState: base_state,
          coveragePct: base_pct,

          // Champs détaillés PENDING-AWARE
          pendingAmount: pending_amount,
          pendingCount: pending_count,
          lastPendingDate: pending_last,

          totalIncludingPending: any_total,
          paymentsCountIncludingPending: any_count,
          lastPaymentDateIncludingPending: any_last,
          coverageStateIncludingPending: any_state,
          coveragePctIncludingPending: any_pct,
        };
      });

      set({ summaries: mapped });
    } catch (e) {
      console.error("[fetchSummaries] error:", e);
      set({ error: "Failed to fetch subscription payment summaries" });
    } finally {
      set({ isLoading: false });
    }
  },
}));
